import { useMutation, useQueryClient } from "@tanstack/react-query";
import calendarEventApiClient, {
  CalendarEvent,
  KLYDO_EVENT_PRIMARY_TYPE,
} from "../../../api/calendarEvent";

/**
 * Takes a klydo out of one scheduler day.
 *
 * An event holds a `klydoIds` array and a single day can carry several klydos,
 * so removing one is a write to that array, NOT a delete of the event — deleting
 * would unschedule every other klydo sharing the day.
 *
 * A Klydo Event left with an empty array is deleted, because empty does NOT make
 * it inert: KlydoSchedulerProvider routes events with no explicit klydos to
 * vector search, and the vector provider generates one on demand for any event
 * id, so the leftover would start serving klydos semantically matched against
 * its own name ("Daily Klydo - 06/09/26"), cached for 7 days.
 *
 * That reasoning holds only for Klydo Events. The lookup this feeds off matches
 * on `klydoIds` with no `primaryType` filter, so the event can just as well be a
 * holiday that pins a curated klydo — there, an empty array is the normal state
 * and vector search is the intended path, and deleting would destroy the
 * holiday's name, country, states and description along with the pin.
 */
const useRemoveKlydoFromScheduler = (klydoId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (event: CalendarEvent) => {
      // Re-read rather than trusting the cached copy the modal was opened with:
      // the upsert `$set`s the whole array, so filtering a stale snapshot would
      // silently revert a klydo someone else added to this day meanwhile.
      const currentEvent = await calendarEventApiClient.getCalendarEvent(
        event.id,
      );
      if (!currentEvent) return;

      const remainingKlydoIds = (currentEvent.klydoIds ?? []).filter(
        (scheduledKlydoId) => scheduledKlydoId !== klydoId,
      );

      if (
        remainingKlydoIds.length === 0 &&
        currentEvent.primaryType === KLYDO_EVENT_PRIMARY_TYPE
      ) {
        return calendarEventApiClient.deleteCalendarEvent(currentEvent.id);
      }

      // The api upserts with a bare object, which mongoose treats as `$set`, so
      // sending only `klydoIds` merges rather than replacing the event.
      return calendarEventApiClient.upsertCalendarEvent(currentEvent.id, {
        klydoIds: remainingKlydoIds,
      });
    },
    onSuccess: () => {
      // The first key is what the klydo modal's scheduler caption reads; the rest
      // are the calendar views that show the event itself.
      queryClient.invalidateQueries({
        queryKey: ["klydo-scheduled-event", klydoId],
      });
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-events-by-date"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-event-list"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-event"] });
    },
  });
};

export default useRemoveKlydoFromScheduler;
